import { cn } from '../lib/cn';
import { t } from '../lib/i18n';
import { formatFileSize, type MediaAttachment } from '../client/media';
import {
  AudioFileIcon,
  DocumentFileIcon,
  DownloadIcon,
  ImageFileIcon,
  VideoFileIcon,
} from './icons';

interface MessageAttachmentProps {
  attachment: MediaAttachment;
}

const ICONS = {
  image: ImageFileIcon,
  video: VideoFileIcon,
  audio: AudioFileIcon,
  document: DocumentFileIcon,
} as const;

/**
 * Downloadable card for a message attachment.
 *
 * This is the universal fallback: every kind renders as a card, so an
 * attachment can never come through as an empty bubble — which is exactly
 * what happened before the widget knew about media at all. Inline previews
 * for image / video / audio land on top of this in B2, falling back here
 * whenever the browser cannot render the type.
 */
export function MessageAttachment({ attachment }: MessageAttachmentProps) {
  const { kind, name, size, url } = attachment;
  const Icon = ICONS[kind] ?? DocumentFileIcon;
  const label = name ?? t('attachment');
  const readableSize = formatFileSize(size);

  return (
    <a
      href={url}
      download={name ?? ''}
      target="_blank"
      // noreferrer matters here beyond the usual tabnabbing concern: the URL
      // carries a media token, and we do not want it travelling in a Referer
      // header to anything the browser opens next.
      rel="noopener noreferrer"
      aria-label={t('download_attachment', { name: label })}
      class={cn(
        'flex items-center gap-[10px] no-underline',
        'px-[10px] py-[8px] rounded-[12px]',
        'bg-black/[0.04] hover:bg-black/[0.07] transition-colors',
        'text-inherit',
      )}
    >
      <Icon class="w-[22px] h-[22px] shrink-0 opacity-70" />
      <span class="flex flex-col min-w-0">
        <span class="text-[13.5px] leading-[1.3] truncate">{label}</span>
        {readableSize && (
          <span class="text-[11px] text-w-secondary leading-[1.3]">
            {readableSize}
          </span>
        )}
      </span>
      <DownloadIcon class="w-[16px] h-[16px] shrink-0 ml-auto opacity-60" />
    </a>
  );
}
